/**
 * Phase 24.7.4 T2 — standalone recipe page for cooking mode.
 *
 * /recipes/:id route. Reuses RecipeDetailContent (extracted from modal).
 * No mark-cooked / swap / feedback — read-only cooking-friendly layout.
 */
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { Card } from "@/components/ui/Card";
import { Skeleton } from "@/components/ui/Skeleton";
import { BackButton } from "@/components/ui/BackButton";
import { RecipeDetailContent } from "@/components/meal_plan/RecipeDetailContent";
import { getRecipe } from "@/api/recipes";

export default function RecipeDetail() {
  const { id } = useParams();
  const { t } = useTranslation();
  const recipeId = parseInt(id || "0", 10);

  const recipeQ = useQuery({
    queryKey: ["recipe", recipeId],
    queryFn: () => getRecipe(recipeId),
    staleTime: 5 * 60_000,
    enabled: recipeId > 0,
  });

  if (recipeQ.isLoading) {
    return (
      <div className="mx-auto max-w-3xl space-y-4 p-4">
        <BackButton />
        <Card>
          <div className="space-y-3" data-testid="recipe-detail-loading">
            <Skeleton className="h-8 w-3/4" />
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-24 w-full" />
          </div>
        </Card>
      </div>
    );
  }

  if (recipeQ.isError || !recipeQ.data) {
    return (
      <div className="mx-auto max-w-3xl space-y-4 p-4">
        <BackButton />
        <Card>
          <p className="text-[13px] text-coral" data-testid="recipe-detail-error">
            {t("meal_plan.recipe_load_failed")}
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl space-y-4 p-4" data-testid="recipe-detail-page">
      <BackButton />
      <Card>
        <h1 className="mb-4 font-serif text-[24px] font-medium leading-tight text-navy-deep">
          {recipeQ.data.name}
        </h1>
        <RecipeDetailContent recipe={recipeQ.data} />
      </Card>
    </div>
  );
}
